import { useEffect, useMemo, useRef, useState } from 'react';
import { Anchor, CircleUserRound, Plane, Ship, ThermometerSun, Users, Zap } from 'lucide-react';
import { MapViewport, type MapMarker } from '../components/MapViewport';
import { Panel, StatusRow, Toggle } from '../components/ui';
import { infrastructureLayer, mobilityLayer } from '../map/data/demoLayers';
import type { WorldViewportHandle } from '../map/WorldViewport';

const baseMarkers: MapMarker[] = [
  { id: 'plane-1', longitude: 10.2, latitude: 50.4, altitude: 10_970, tone: 'blue', glyph: '✈', label: 'LH742' },
  { id: 'plane-2', longitude: 35.6, latitude: 39.4, altitude: 11_580, tone: 'blue', glyph: '✈', label: 'EK208' },
  { id: 'plane-3', longitude: 77.5, latitude: 28.2, altitude: 11_275, tone: 'blue', glyph: '✈', label: 'AI302' },
  { id: 'plane-4', longitude: 112.3, latitude: 34.6, altitude: 10_660, tone: 'blue', glyph: '✈', label: 'CA968' },
  { id: 'plane-5', longitude: 103.8, latitude: 8.4, altitude: 11_275, tone: 'blue', glyph: '✈', label: 'SQ221' },
  { id: 'port-1', longitude: 72.88, latitude: 19.07, tone: 'green', glyph: '⚓', label: 'Mumbai Port' },
  { id: 'port-2', longitude: 121.47, latitude: 31.23, tone: 'green', glyph: '⚓', label: 'Shanghai Port' },
  { id: 'city-1', longitude: 77.10, latitude: 28.70, tone: 'cyan', glyph: '●', label: 'Delhi' },
  { id: 'city-2', longitude: 23.73, latitude: 37.98, tone: 'cyan', glyph: '●', label: 'Athens' },
];

export function EarthViewPage() {
  const mapController = useRef<WorldViewportHandle | null>(null);
  const [layers, setLayers] = useState<Record<string, boolean>>({
    Borders: false,
    Cities: false,
    Infrastructure: false,
    'Air Traffic': true,
    'Maritime Traffic': true,
    Weather: false,
    Terrain: false,
    'Population Density': false,
    'Night Lights': true,
  });
  const [speed, setSpeed] = useState('1x');
  const [isPlaying, setPlaying] = useState(false);
  const [timeProgress, setTimeProgress] = useState(50);
  const visibleMarkers = useMemo(
    () => baseMarkers.filter((marker) => marker.id.startsWith('plane') ? layers['Air Traffic'] : marker.id.startsWith('port') ? layers['Maritime Traffic'] : layers.Cities),
    [layers],
  );
  const mapLayers = useMemo(() => [
    { ...mobilityLayer, visible: layers['Air Traffic'] },
    { ...infrastructureLayer, visible: layers.Infrastructure },
  ], [layers]);
  const currentTime = useMemo(() => {
    const start = Date.parse('2025-05-14T00:00:00Z');
    const end = Date.parse('2025-05-26T23:59:59Z');
    return new Date(start + (end - start) * timeProgress / 100).toISOString();
  }, [timeProgress]);
  useEffect(() => {
    if (!isPlaying) return undefined;
    const multiplier = Number.parseFloat(speed);
    const timer = window.setInterval(() => {
      setTimeProgress((value) => {
        const next = value + 0.22 * multiplier;
        if (next >= 100) { setPlaying(false); return 100; }
        return next;
      });
    }, 250);
    return () => window.clearInterval(timer);
  }, [isPlaying, speed]);
  return (
    <div className="earth-page">
      <MapViewport markers={visibleMarkers} layers={mapLayers} showCountries={layers.Borders} showNightLights={layers['Night Lights']} initialCamera={{ longitude: 55, latitude: 30, height: 13_000_000, pitch: -90 }} controllerRef={mapController} time={{ mode: isPlaying ? 'historical' : 'live', currentTime, playbackSpeed: Number.parseFloat(speed) }} className="earth-page__viewport">
        <Panel className="floating-status" title={<span className="eyebrow">Planet status</span>}>
          <StatusRow icon={<CircleUserRound size={15} />} label="Population" value="8,102,344,514" detail="+0.95%" tone="purple" />
          <StatusRow icon={<ThermometerSun size={15} />} label="Global temp" value="+1.32 °C" detail="+0.05 °C" tone="orange" />
          <StatusRow icon={<Zap size={15} />} label="Energy demand" value="18,760 GW" detail="+2.1%" tone="cyan" />
          <div className="alert-summary alert-summary--compact"><div><strong className="text-red">12</strong><span>Critical</span></div><div><strong className="text-yellow">48</strong><span>Warning</span></div><div><strong className="text-blue">128</strong><span>Info</span></div></div>
        </Panel>
        <div className="playback-bar">
          <button type="button" onClick={() => setPlaying((value) => !value)} aria-label={isPlaying ? 'Pause timeline' : 'Play timeline'}>{isPlaying ? 'Ⅱ' : '▶'}</button>
          <strong>{new Date(currentTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</strong>
          <span>{new Date(currentTime).toLocaleTimeString('en-GB', { hour12: false, timeZone: 'UTC' })} UTC</span>
          <input type="range" min={0} max={100} value={timeProgress} onChange={(event) => setTimeProgress(Number(event.target.value))} />
          <select value={speed} onChange={(event) => setSpeed(event.target.value)}><option>0.5x</option><option>1x</option><option>2x</option><option>4x</option></select>
        </div>
      </MapViewport>
      <aside className="earth-page__right">
        <Panel title={<span className="eyebrow">Layers</span>}>
          <div className="toggle-list">
            {Object.entries(layers).map(([label, checked]) => <Toggle key={label} label={label} checked={checked} onChange={(value) => setLayers((current) => ({ ...current, [label]: value }))} />)}
          </div>
        </Panel>
        <Panel title={<span className="eyebrow">Camera controls</span>}>
          <div className="camera-card">
            <div className="camera-dial"><span>N</span><strong>◇</strong></div>
            <div><button type="button" onClick={() => mapController.current?.zoomBy(1.35)}>＋ Zoom In</button><button type="button" onClick={() => mapController.current?.zoomBy(0.74)}>− Zoom Out</button><button type="button" onClick={() => mapController.current?.resetCamera()}>↻ Reset View</button><button type="button" onClick={() => mapController.current?.setAutoRotate(true)}>⛶ Auto Rotate</button></div>
          </div>
        </Panel>
        <Panel title={<span className="eyebrow">Active streams</span>}>
          <div className="stream-pills"><span><Plane size={14} /> 128 routes</span><span><Ship size={14} /> 42 lanes</span><span><Anchor size={14} /> 15 ports</span><span><Users size={14} /> 239 regions</span></div>
        </Panel>
      </aside>
      <Panel className="earth-timeline" title={<span className="eyebrow">Timeline</span>} action={<span><i className="live-dot" /> Live</span>}>
        <div className="timeline-days">
          {['May 14', 'May 15', 'May 16', 'May 17', 'May 18', 'May 19', 'Today', 'May 21', 'May 22', 'May 24'].map((day, index, all) => <button type="button" key={day} onClick={() => setTimeProgress(index / (all.length - 1) * 100)} className={Math.abs(timeProgress - index / (all.length - 1) * 100) < 6 ? 'is-active' : ''}>{day}<small>{day === 'Today' ? 'May 20' : ''}</small></button>)}
        </div>
      </Panel>
    </div>
  );
}
